import { useState, useRef } from 'external/react';
import PayoutsExportButton from './PayoutsExportButton';
import Icon from 'external/fortawesome';

interface TableToolbarProps {
  pattern?: string;
  from?: string;
  to?: string;
  onSearch: (pattern: string | undefined) => void;
  onDateRange: (from: string | undefined, to: string | undefined) => void;
}

const today = new Date();
const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
const toDateString = (d: Date) => d.toISOString().split('T')[0];

const PayoutsTableToolbar = ({ pattern, from, to, onSearch, onDateRange }: TableToolbarProps) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [dateOpen, setDateOpen] = useState(false);
  const [fromDraft, setFromDraft] = useState(from ?? toDateString(firstOfMonth));
  const [toDraft, setToDraft] = useState(to ?? toDateString(today));
  const inputRef = useRef<HTMLInputElement>(null);

  const openSearch = () => {
    setSearchOpen(true);
    setDraft(pattern ?? '');
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const commitSearch = () => {
    onSearch(draft || undefined);
    setSearchOpen(false);
    setDraft('');
  };

  const clearPattern = () => {
    onSearch(undefined);
    setSearchOpen(false);
    setDraft('');
  };

  const commitDateRange = () => {
    onDateRange(fromDraft || undefined, toDraft || undefined);
    setDateOpen(false);
  };

  const clearDateRange = () => {
    setFromDraft('');
    setToDraft('');
    onDateRange(undefined, undefined);
    setDateOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') commitSearch();
    if (e.key === 'Escape') setSearchOpen(false);
  };

  const hasDateFilter = from || to;

  return (
    <div className="d-flex align-items-center justify-content-between mb-3">
      {/* Left: search + date range */}
      <div className="d-flex align-items-center gap-2">

        {/* Text search */}
        {pattern && !searchOpen && (
          <span className="badge bg-primary d-flex align-items-center gap-1" style={{ fontSize: '0.85rem' }}>
            🔍 {pattern}
            <button onClick={clearPattern} className="btn-close btn-close-white ms-1" style={{ fontSize: '0.6rem' }} />
          </span>
        )}
        {searchOpen ? (
          <div className="d-flex align-items-center gap-1">
            <input
              ref={inputRef}
              className="form-control form-control-sm"
              style={{ width: 200 }}
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Buscar..."
            />
            <button className="btn btn-sm btn-primary" onClick={commitSearch}>Aplicar</button>
            <button className="btn btn-sm btn-outline-secondary" onClick={() => setSearchOpen(false)}>✕</button>
          </div>
        ) : (
          <button className="btn btn-sm btn-outline-secondary" onClick={openSearch} title="Buscar">
            🔍
          </button>
        )}

        {/* Date range */}
        <div style={{ position: 'relative' }}>
          <button
            className={`btn btn-sm ${hasDateFilter ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => setDateOpen(o => !o)}
            title="Filtrar por fecha"
          >
            <Icon name="faCalendarDays" />
            {hasDateFilter && (
              <span className="ms-1" style={{ fontSize: '0.8rem' }}>
                {from ?? '...'} — {to ?? '...'}
              </span>
            )}
          </button>

          {dateOpen && (
            <div style={{
              position: 'absolute', left: 0, top: '110%', zIndex: 100,
              background: 'white', border: '1px solid #dee2e6', borderRadius: 6,
              padding: '12px 16px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              minWidth: 260,
            }}>
              <div className="mb-2">
                <label className="form-label small mb-1">Desde</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={fromDraft}
                  onChange={e => setFromDraft(e.target.value)}
                />
              </div>
              <div className="mb-3">
                <label className="form-label small mb-1">Hasta</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={toDraft}
                  onChange={e => setToDraft(e.target.value)}
                />
              </div>
              <div className="d-flex gap-2">
                <button className="btn btn-sm btn-primary w-100" onClick={commitDateRange}>Aplicar</button>
                <button className="btn btn-sm btn-outline-secondary" onClick={clearDateRange}>Limpiar</button>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setDateOpen(false)}>✕</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right: export */}
      <PayoutsExportButton />
    </div>
  );
};

export default PayoutsTableToolbar;